import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send, X, MessagesSquare } from 'lucide-react';
import { salesDeskAPI } from '../../lib/api';

// Manager-side view of the EXISTING salesperson<->manager side-channel
// (sales_manager_chat). Reuses salesDeskAPI — no second messaging system.
// Rendered INLINE inside the inquiry dialog so the buyer conversation stays
// visible above it (no overlay/stacking/focus-trap issues with the Radix dialog).
export function ManagerSalesChat({ organizationId, onClose }) {
    const { t } = useTranslation();
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        try { const r = await salesDeskAPI.managerChat(organizationId); setMsgs(r.data?.messages || []); } catch { /* noop */ }
    }, [organizationId]);
    useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

    const send = async () => {
        const body = input.trim();
        if (!body || sending) return;
        setSending(true);
        try { await salesDeskAPI.sendManagerChat(organizationId, body); setInput(''); await load(); } catch { /* noop */ }
        setSending(false);
    };

    return (
        <div className="rounded-xl border border-orange-500/30 bg-orange-500/[0.04] overflow-hidden" data-testid="manager-sales-chat">
            <header className="flex items-center justify-between px-3 py-2 border-b border-orange-500/20">
                <div className="flex items-center gap-2">
                    <MessagesSquare className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                    <span className="text-xs font-bold text-orange-600 dark:text-orange-300">{t('commandCenter.automotive.salesChatTitle', 'Message sales')}</span>
                </div>
                <button onClick={onClose} data-testid="manager-sales-chat-close" className="text-slate-400 hover:text-slate-200 transition-colors"><X className="w-4 h-4" /></button>
            </header>
            <p className="px-3 pt-2 text-[11px] text-slate-500 dark:text-slate-400">{t('commandCenter.automotive.salesChatHint', 'Private channel with your salesperson — the buyer never sees this.')}</p>
            <div className="max-h-48 overflow-y-auto px-3 py-2 space-y-2" data-testid="manager-sales-chat-messages">
                {msgs.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">{t('commandCenter.automotive.salesChatEmpty', 'No messages yet')}</p>}
                {msgs.map((m) => {
                    const mine = m.sender_role === 'manager';
                    return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${mine ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'}`}>
                                <p className="text-[10px] font-semibold mb-0.5 text-white/80">{mine ? t('commandCenter.automotive.roleManager', 'Manager') : t('commandCenter.automotive.roleSalesperson', 'Salesperson')}</p>
                                <p className="leading-snug whitespace-pre-wrap break-words">{m.body}</p>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 px-3 py-2 border-t border-orange-500/20">
                <input value={input} onChange={(e) => setInput(e.target.value)} autoFocus
                    data-testid="manager-sales-chat-input" placeholder={t('commandCenter.automotive.salesChatPlaceholder', 'Message your salesperson…')}
                    className="flex-1 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-orange-500" />
                <button type="submit" disabled={sending || !input.trim()} data-testid="manager-sales-chat-send"
                    className="shrink-0 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white rounded-lg px-3 py-2 flex items-center gap-1.5 text-sm">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </form>
        </div>
    );
}

export default ManagerSalesChat;
